/**
 * Externship recommendations — the `profile` (final) view.
 *
 * REAL, CURRENTLY-OPEN FINANCE EXTERNSHIPS (sourced 2026-06-14 from Extern's prod MySQL replica —
 * `externships` rows with status='ACTIVE' and future start dates, category 'Finance'). The five
 * live finance programs at that time, with their real titles, durations, skills/topics, and the
 * deliverables from each program's `details.responsibilities`:
 *
 *   • Attronica — FP&A & Private Company Valuation        (8 wk,  start 2026-06-15)  → corporate finance
 *   • Yinan Zhao — Value Investing & Financial Modeling   (11 wk, start 2026-06-22)  → equity research
 *   • HP Tech Ventures — Deal Sourcing & Startup Analysis (8 wk,  start 2026-06-22)  → corporate VC
 *   • Mangusta Capital — Deal Sourcing & Startup Analysis (8 wk,  start 2026-06-15)  → Vertical-AI VC
 *   • Energy Innovation Capital — VC in Climate & Energy  (8 wk,  start 2026-06-29)  → climate VC
 *
 * Each card carries the program's REAL company logo (`logo`, trimmed from the replica's
 * `companies.logo` S3 mark) and a REAL hero image (`image`, downscaled from the program's
 * `details.top_image`) — both in `public/images/rec/{logos,heroes}/`. `tags` / `whatYoullLearn` are derived
 * faithfully from each program's recorded skills/topics + responsibilities (concrete deliverables
 * only — no invented content). 3 cards per orientation = 9 slots; companies repeat across orientations
 * (e.g. Yinan Zhao, HP Tech Ventures) with orientation-specific blurbs. Selected by `state.archetype`
 * (M2), fallback `gen`.
 *
 * CTAs route to the evergreen finance catalog (`CATALOG_URL`) — the replica has no per-cohort slug,
 * and cohort-dated detail URLs rotate, so the catalog is the stable target. Source: docs/EXPERIENCE-MODEL.md §6.
 */

import type { ArchetypeId } from './archetypes';

export interface Recommendation {
  /** One-paragraph program blurb (orientation-specific). */
  blurb: string;
  /** Recommending company / mentor (e.g. "Attronica", "Yinan Zhao", "Energy Innovation Capital"). */
  company: string;
  /** Program length, e.g. "8 weeks". Rendered as its own DurationTag chip (kept OUT of `tags`). */
  duration: string;
  /**
   * Live catalog externship id, when this rec came from the live catalog. Present → the card CTA
   * deep-links to that program's real detail page (`/externships/available/{id}`) so the learner can
   * enroll/start immediately. Absent (static-fallback rows) → the CTA opens the evergreen catalog.
   */
  externshipId?: number;
  /** Real program hero image (downscaled from the program's `details.top_image`) under public/images/rec/heroes/. */
  image: string;
  /** Real company logo (trimmed brand lockup) under public/images/rec/logos/ — rendered as the card's logo chip. */
  logo: string;
  /** Skill / topic chips shown on every card. Real skills + topics from the program's replica record. */
  tags: readonly string[];
  /** Full externship title. */
  title: string;
  /** "What you'll learn" outcomes (best-match card). Each bullet is a real deliverable from the program. */
  whatYoullLearn: readonly string[];
}

/**
 * Per-position fallback thumbnails (generic finance photography), kept ONLY as a safety net if a
 * `Recommendation.image` is ever missing. Every real program below sets its own `image`, so these
 * are not used in practice.
 */
export const REC_IMAGES = {
  best: '/images/rec/best-match.png',
  cards: ['/images/rec/card-1.png', '/images/rec/card-2.png'] as const,
} as const;

// ── Real assets per program (logo lockup + program hero), pulled from the replica + program pages. ──
const ASSET = {
  attronica: { logo: '/images/rec/logos/attronica.png', image: '/images/rec/heroes/attronica.jpg' },
  yinan: { logo: '/images/rec/logos/yinan.png', image: '/images/rec/heroes/yinan.jpg' },
  hp: { logo: '/images/rec/logos/hp.png', image: '/images/rec/heroes/hp.jpg' },
  mangusta: { logo: '/images/rec/logos/mangusta.png', image: '/images/rec/heroes/mangusta.jpg' },
  eic: { logo: '/images/rec/logos/eic.png', image: '/images/rec/heroes/eic.jpg' },
} as const;

export const RECOMMENDATIONS: Record<ArchetypeId, readonly Recommendation[]> = {
  // THE SCALER — corporate finance / FP&A / operator-side.
  corp: [
    {
      company: 'Attronica',
      title: 'Financial Planning & Analysis and Private Company Valuation',
      duration: '8 weeks',
      blurb:
        'Value a real 42-year-old firm end to end: clean its financials, model a growth plan, and hand the founder a defensible DCF + comps valuation.',
      tags: ['FP&A', 'Valuation', 'DCF & Comps', 'Financial Modeling', 'Excel'],
      whatYoullLearn: [
        "Analyze a real company's financial health vs. peers",
        'Build driver-based growth and cash projections',
        'Value a division with DCF + comparable companies',
        'Deliver a 4-page strategic recommendation',
      ],
      ...ASSET.attronica,
    },
    {
      company: 'Yinan Zhao',
      title: 'Value Investing & Financial Modeling',
      duration: '11 weeks',
      blurb:
        'Build the modeling muscle of a finance operator — take a public company from its filings to a full DCF + comps valuation and price target.',
      tags: ['Financial Modeling', 'Valuation', 'Value Investing', 'Excel'],
      whatYoullLearn: [
        'Read SEC filings and judge financial health',
        'Build a DCF + comps valuation model',
        'Forecast performance and a price target',
        'Write up the analysis in a memo',
      ],
      ...ASSET.yinan,
    },
    {
      company: 'HP Tech Ventures',
      title: 'Deal Sourcing & Startup Analysis',
      duration: '8 weeks',
      blurb:
        'See how a corporate venture arm allocates capital: source startups, size their markets, and write the memo behind a real check.',
      tags: ['Venture Capital', 'Deal Sourcing', 'Startup Research', 'Investment Memo'],
      whatYoullLearn: [
        'Source innovative tech startups',
        'Research markets, teams, and financials',
        'Run a competitor analysis',
        'Write an investment memo',
      ],
      ...ASSET.hp,
    },
  ],
  // THE ANALYST — investing / equity research / venture.
  invest: [
    {
      company: 'Yinan Zhao',
      title: 'Value Investing & Financial Modeling',
      duration: '11 weeks',
      blurb:
        'Cover one name end to end as an equity research analyst — dissect the filings, model the business, and present a buy/sell investment memo.',
      tags: ['Value Investing', 'Equity Research', 'Financial Modeling', 'Valuation'],
      whatYoullLearn: [
        'Analyze SEC filings and financial health',
        'Identify risks and market catalysts',
        'Build DCF + comps valuation models',
        'Write and present an investment memo',
      ],
      ...ASSET.yinan,
    },
    {
      company: 'Mangusta Capital',
      title: 'Deal Sourcing & Startup Analysis',
      duration: '8 weeks',
      blurb:
        'Work as a venture analyst on a live SoftBank-pedigree fund — source Vertical-AI startups, run diligence, and pitch a 3-slide memo to the partners.',
      tags: ['Venture Capital', 'Vertical AI', 'Deal Sourcing', 'Due Diligence'],
      whatYoullLearn: [
        'Source Vertical-AI startups',
        'Run founder and market diligence',
        'Build a competitor analysis',
        'Pitch a partner-ready investment memo',
      ],
      ...ASSET.mangusta,
    },
    {
      company: 'Energy Innovation Capital',
      title: 'Venture Capital Investing in Climate & Energy Tech',
      duration: '8 weeks',
      blurb:
        'Invest like a climate-tech VC: source energy and industrial startups, judge the technology and founders, and build an investor-ready memo.',
      tags: ['Venture Capital', 'Climate & Energy', 'Deal Sourcing', 'Investment Memo'],
      whatYoullLearn: [
        'Source climate and industrial startups',
        'Evaluate technology and founders',
        'Run competitive analysis with AI tools',
        'Build an investor-ready memo',
      ],
      ...ASSET.eic,
    },
  ],
  // THE POLYMATH — all-rounder / beginner-friendly on-ramp.
  gen: [
    {
      company: 'HP Tech Ventures',
      title: 'Deal Sourcing & Startup Analysis',
      duration: '8 weeks',
      blurb:
        'A beginner-friendly on-ramp into finance via venture capital — find startups, size a market, and ship a decision-ready investment memo.',
      tags: ['Venture Capital', 'Beginner-Friendly', 'Deal Sourcing', 'Investment Memo'],
      whatYoullLearn: [
        'Find and evaluate early-stage startups',
        'Learn how investors size a market',
        'Compare startups against competitors',
        'Ship a decision-ready memo',
      ],
      ...ASSET.hp,
    },
    {
      company: 'Attronica',
      title: 'Financial Planning & Analysis and Private Company Valuation',
      duration: '8 weeks',
      blurb:
        'Real corporate finance on a real business — read the financials, model a growth plan, and value a division for the founder.',
      tags: ['FP&A', 'Valuation', 'Forecasting', 'Financial Statements'],
      whatYoullLearn: [
        "Read a real company's financials",
        'Build a forecast and value a division',
        'Benchmark performance against peers',
        'Present the case to the founder',
      ],
      ...ASSET.attronica,
    },
    {
      company: 'Yinan Zhao',
      title: 'Value Investing & Financial Modeling',
      duration: '11 weeks',
      blurb:
        'The all-rounder finance program — research a public company, model it in Excel, and value it three ways in an investment memo.',
      tags: ['Financial Modeling', 'Value Investing', 'Valuation', 'Excel'],
      whatYoullLearn: [
        'Research a public company',
        'Model it in Excel',
        'Value it three ways',
        'Write the investment memo',
      ],
      ...ASSET.yinan,
    },
  ],
};

/**
 * In-app finance catalog path — the CTA fallback for static rows that carry no live `externshipId`
 * (live rows deep-link to `/externships/available/{id}` instead; see `Recommendation.externshipId`).
 * Kept RELATIVE/internal (NOT an absolute `extern.com` URL) so the CTA never bleeds dev → prod and the
 * authenticated learner stays in-app — the prod URL hardcode was flagged in the Jun-26 demo.
 */
export const CATALOG_PATH = '/externships/available?categories=Finance';

/** In-app path to a live externship's real detail/enroll page (the catalog deep-link target). */
export function externshipHref(externshipId: number): string {
  return `/externships/available/${externshipId}`;
}
